import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown } from 'react-icons/fi';
import { useEditorStore } from '../../stores/editorStore';
import type { Language } from '../../types/editor';
import styles from './LanguageSwitcher.module.css';

interface LangOption {
  code: Language;
  flag: string;
  nativeName: string;
}

const LANGUAGES: LangOption[] = [
  { code: 'tr', flag: '🇹🇷', nativeName: 'Türkçe' },
  { code: 'en', flag: '🇬🇧', nativeName: 'English' },
  { code: 'ar', flag: '🇸🇦', nativeName: 'العربية' },
];

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const language = useEditorStore((s) => s.language);
  const setLanguage = useEditorStore((s) => s.setLanguage);

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];

  const handleSelect = useCallback(
    (code: Language) => {
      setLanguage(code);
      setOpen(false);
    },
    [setLanguage]
  );

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={() => setOpen((p) => !p)}
        title={t('language.select')}
      >
        <span className={styles.flag}>{current.flag}</span>
        <span>{current.nativeName}</span>
        <FiChevronDown
          className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
        />
      </button>

      {open && (
        <div className={styles.dropdown}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`${styles.option} ${
                lang.code === language ? styles.optionActive : ''
              }`}
              onClick={() => handleSelect(lang.code)}
            >
              <span className={styles.flag}>{lang.flag}</span>
              <span className={styles.optionLabel}>{lang.nativeName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
